import React, { useState, useMemo, useEffect, useRef } from 'react';
import axios from '../../utils/axiosConfig';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
  Popover,
  Button,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { format } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PrintIcon from '@mui/icons-material/Print';
import CloudIcon from '@mui/icons-material/Cloud';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HospitalSearch from './HospitalSearch';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';
import WeatherSearch from './WeatherSearch';
import useNotesAutoFill from './hooks/useNotesAutoFill';

// Inline editable text field component
const EditableField = ({ value, onChange, label, multiline = false, type = 'text', sx = {} }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempValue, setTempValue] = useState(value || '');
  const theme = useTheme();

  const handleClick = (event) => {
    setTempValue(value || '');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSave = () => {
    onChange(tempValue);
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          borderRadius: 1,
          px: 0.5,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
          ...sx
        }}
      >
        <Typography component="span" sx={{ ...sx }}>
          {value || <em style={{ color: theme.palette.text.disabled }}>Click to edit</em>}
        </Typography>
        <EditIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.5 }} />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {label}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            multiline={multiline}
            rows={multiline ? 3 : 1}
            type={type}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !multiline) {
                handleSave();
              }
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleClose}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleSave}>Save</Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// Inline editable time field
const EditableTimeField = ({ value, onChange, label }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempValue, setTempValue] = useState(value || '');
  const theme = useTheme();

  const handleClick = (event) => {
    setTempValue(value || '');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSave = () => {
    onChange(tempValue);
    handleClose();
  };

  const open = Boolean(anchorEl);

  const formatTimeDisplay = (timeString) => {
    if (!timeString) return '--:--';
    try {
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date();
      date.setHours(hours);
      date.setMinutes(minutes);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toUpperCase();
    } catch (e) {
      return timeString;
    }
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          borderRadius: 1,
          px: 0.5,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          }
        }}
      >
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          {formatTimeDisplay(value)}
        </Typography>
        <EditIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.5 }} />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {label}
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="time"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            autoFocus
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleClose}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleSave}>Save</Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// Weather icon helper
const getWeatherIcon = (condition) => {
  const c = (condition || '').toLowerCase();
  if (/(thunder|storm)/.test(c)) return '⛈️';
  if (/(rain|shower|drizzle)/.test(c)) return '🌧️';
  if (/(snow|sleet)/.test(c)) return '❄️';
  if (/(fog|haze)/.test(c)) return '🌫️';
  if (/(cloud)/.test(c)) return '☁️';
  if (/(wind)/.test(c)) return '🌬️';
  if (/(clear|sunny)/.test(c)) return '☀️';
  return '🌤️';
};

// Parse weather metrics from notes
const parseWeatherMetrics = (notes) => {
  if (!notes || typeof notes !== 'string') return {};
  try {
    const src = notes.replace(/\s+/g, ' ').trim();
    const tempMatch = src.match(/(\b-?\d{1,3})\s*°\s*([FC])/i);
    const tempVal = tempMatch ? tempMatch[1] : null;
    const tempUnit = tempMatch ? tempMatch[2].toUpperCase() : 'F';
    const condMatch = src.match(/\b(Sunny|Clear|Cloudy|Overcast|Rain|Showers|Thunderstorms|Storms|Snow|Sleet|Drizzle|Windy|Fog|Haze|Partly Cloudy|Mostly Cloudy)\b/i);
    const condition = condMatch ? condMatch[1] : null;
    const windMatch = src.match(/Wind[^:]*:\s*([\d.]+)\s*(mph|km\/h)/i) || src.match(/\b([\d.]+)\s*(mph|km\/h)\b/i);
    const windVal = windMatch ? windMatch[1] : null;
    const windUnit = windMatch ? windMatch[2] : null;
    const humMatch = src.match(/Humidity[^:]*:\s*(\d{1,3})%/i) || src.match(/\b(\d{1,3})%\b/);
    const humidity = humMatch ? humMatch[1] : null;
    return { tempVal, tempUnit, condition, windVal, windUnit, humidity };
  } catch {
    return {};
  }
};

// Format pages to eighths
const formatPagesEighths = (pages) => {
  if (pages === null || pages === undefined) return '';
  if (typeof pages === 'string') {
    const s = pages.trim();
    if (!s) return '';
    const matchWholeAndFrac = s.match(/^(\d+)\s+(\d)\/8$/);
    if (matchWholeAndFrac) {
      const frac = parseInt(matchWholeAndFrac[2], 10);
      return frac === 0 ? '8/8' : `${frac}/8`;
    }
    const matchFracOnly = s.match(/^(\d)\/8$/);
    if (matchFracOnly) {
      const frac = parseInt(matchFracOnly[1], 10);
      return frac === 0 ? '' : `${frac}/8`;
    }
    const matchWholeOnly = s.match(/^(\d+)$/);
    if (matchWholeOnly) {
      const whole = parseInt(matchWholeOnly[1], 10);
      return whole === 0 ? '' : '8/8';
    }
  }
  const num = typeof pages === 'number' ? pages : parseFloat(String(pages));
  if (isNaN(num) || num === 0) return '';
  const totalEighths = Math.round(num * 8);
  if (totalEighths <= 0) return '';
  const frac = totalEighths % 8;
  return frac === 0 ? '8/8' : `${frac}/8`;
};

/**
 * CallsheetComposeView - A WYSIWYG editable view that mirrors the PDF layout
 */
const CallsheetComposeView = ({ 
  data, 
  onUpdate, 
  onPrint,
  projectId,
  locationsVersion 
}) => {
  const theme = useTheme();
  
  // Dialog states for Google Maps search
  const [hospitalSearchOpen, setHospitalSearchOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationDialogType, setLocationDialogType] = useState('basecamp'); // 'basecamp', 'parking'
  const [tempLocationValue, setTempLocationValue] = useState('');
  
  // Add Producer dialog state
  const [addProducerOpen, setAddProducerOpen] = useState(false);
  const [newProducerType, setNewProducerType] = useState('Executive Producer');
  const [newProducerName, setNewProducerName] = useState('');
  
  // Weather search dialog state
  const [weatherSearchOpen, setWeatherSearchOpen] = useState(false);
  
  // Locations for schedule dropdown
  const [locations, setLocations] = useState([]);
  
  // Project cast for talent calls
  const [projectCast, setProjectCast] = useState([]);
  const [addTalentDialogOpen, setAddTalentDialogOpen] = useState(false);
  
  // Project crew for crew calls
  const [projectCrew, setProjectCrew] = useState([]);
  const [addCrewDialogOpen, setAddCrewDialogOpen] = useState(false);
  
  // Auto-fill notes from shooting schedule
  const { suggestions: notesSuggestions } = useNotesAutoFill({ projectId, shootingDay: data.shootingDay });
  
  // Auto-apply notes suggestions when day changes - ALWAYS REPLACE notes with current day's suggestions
  const notesSignatureRef = useRef('');
  useEffect(() => {
    if (!notesSuggestions || !projectId) return;
    
    const currentDay = data?.shootingDay || 1;
    const sig = JSON.stringify({ day: currentDay, notesSuggestions });
    if (notesSignatureRef.current === sig) return;
    
    const map = [
      { key: 'camera', field: 'cameraNotes' },
      { key: 'lighting', field: 'lightingNotes' },
      { key: 'props', field: 'propsNotes' },
      { key: 'sound', field: 'soundNotes' },
      { key: 'vfx', field: 'vfxNotes' },
      { key: 'specialFx', field: 'specialFxNotes' },
      { key: 'vehicles', field: 'vehiclesNotes' },
      { key: 'stunts', field: 'stuntsNotes' },
      { key: 'animals', field: 'animalsNotes' },
      { key: 'hairMakeup', field: 'hairMakeupNotes' },
      { key: 'misc', field: 'miscNotes' }
    ];
    
    // ALWAYS replace notes with current day's suggestions (no appending)
    const updates = {};
    let hasChange = false;
    
    map.forEach(({ key, field }) => {
      const items = notesSuggestions[key] || [];
      const newValue = items.join(', ');
      // Only update if value is different
      if (newValue !== (data[field] || '')) {
        updates[field] = newValue;
        hasChange = true;
      }
    });
    
    if (hasChange) {
      notesSignatureRef.current = sig;
      console.log('CallsheetComposeView: Replacing notes for day', currentDay, updates);
      onUpdate(updates);
    }
  }, [notesSuggestions, data?.shootingDay, projectId, data, onUpdate]);

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      if (!projectId) return;
      try {
        const { data: locationsData } = await axios.get(`/api/v1/locations/${projectId}`);
        const formattedLocations = locationsData.map(location => ({
          name: location.name,
          address: location.address || ''
        }));
        // Add fallback locations if none available
        if (formattedLocations.length === 0) {
          formattedLocations.push(
            { name: 'Studio', address: '' },
            { name: 'Location TBD', address: '' },
            { name: 'Exterior', address: '' },
            { name: 'Interior', address: '' }
          );
        }
        setLocations(formattedLocations);
      } catch (error) {
        console.error('Error fetching locations:', error);
        setLocations([
          { name: 'Studio', address: '' },
          { name: 'Location TBD', address: '' },
          { name: 'Exterior', address: '' },
          { name: 'Interior', address: '' }
        ]);
      }
    };
    fetchLocations();
  }, [projectId, locationsVersion]);

  // Fetch project cast for talent calls
  useEffect(() => {
    const fetchProjectCast = async () => {
      if (!projectId) return;
      try {
        const { data: castData } = await axios.get(`/api/v1/cast/project/${projectId}`);
        setProjectCast(Array.isArray(castData) ? castData : []);
      } catch (error) {
        console.error('Error fetching project cast:', error);
        setProjectCast([]);
      }
    };
    fetchProjectCast();
  }, [projectId]);

  // Fetch project crew for crew calls
  useEffect(() => {
    const fetchProjectCrew = async () => {
      if (!projectId) return;
      try {
        const { data: crewData } = await axios.get(`/api/v1/crew/projects/${projectId}/crew`);
        setProjectCrew(Array.isArray(crewData) ? crewData : []);
      } catch (error) {
        console.error('Error fetching project crew:', error);
        setProjectCrew([]);
      }
    };
    fetchProjectCrew();
  }, [projectId]);

  // Format date for display
  const formattedDate = data.date 
    ? format(new Date(data.date), 'EEEE, MMMM d, yyyy') 
    : 'Date not set';

  // Convert character names to cast numbers for schedule display
  const getCastNumbers = (castData) => {
    if (!castData) return '';
    const castArray = Array.isArray(castData) 
      ? castData 
      : (typeof castData === 'string' ? castData.split(',').filter(Boolean).map(c => c.trim()) : []);
    
    if (castArray.length === 0) return '';
    
    const numbers = castArray.map(charName => {
      const castMember = projectCast.find(
        cast => cast.characterName && 
        cast.characterName.toLowerCase() === charName.toLowerCase()
      );
      return castMember ? castMember.hierarchy : null;
    }).filter(n => n !== null);
    
    return numbers.length > 0 ? numbers.join(', ') : '';
  };

  // Parse weather data
  const wx = parseWeatherMetrics(data.weatherNotes || '');
  const condIcon = getWeatherIcon(wx.condition);

  // Format time display - handles both HH:MM strings and ISO date strings
  const formatTimeDisplay = (timeString) => {
    if (!timeString) return '--:--';
    try {
      // Check if it's an ISO date string
      if (timeString.includes('T') || timeString.includes('-')) {
        const date = new Date(timeString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }).toUpperCase();
        }
      }
      // Handle HH:MM format
      const [hours, minutes] = timeString.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) return timeString;
      const date = new Date();
      date.setHours(hours);
      date.setMinutes(minutes);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toUpperCase();
    } catch (e) {
      return timeString;
    }
  };

  // Group crew by department
  const crewByDepartment = useMemo(() => {
    if (!data.crewCalls || !Array.isArray(data.crewCalls)) return {};
    return data.crewCalls.reduce((acc, crew) => {
      const dept = crew.department || 'Other';
      if (!acc[dept]) acc[dept] = [];
      acc[dept].push(crew);
      return acc;
    }, {});
  }, [data.crewCalls]);

  // Handlers for inline editing
  const handleFieldUpdate = (field, value) => {
    onUpdate({ [field]: value });
  };

  const handleNestedUpdate = (parent, field, value) => {
    onUpdate({
      [parent]: {
        ...(data[parent] || {}),
        [field]: value
      }
    });
  };

  // Hospital search handlers
  const handleSelectHospital = (hospital) => {
    const hospitalInfo = `${hospital.name} - ${hospital.address}${hospital.phoneNumber ? ` - ${hospital.phoneNumber}` : ''}${hospital.distance ? ` (${hospital.distance} km away)` : ''}`;
    handleNestedUpdate('emergencyContacts', 'hospital', hospitalInfo);
    setHospitalSearchOpen(false);
  };

  // Location dialog handlers
  const openLocationDialog = (type, currentValue) => {
    setLocationDialogType(type);
    setTempLocationValue(currentValue || '');
    setLocationDialogOpen(true);
  };

  const handleLocationSelect = (place) => {
    if (place && place.formatted_address) {
      setTempLocationValue(place.formatted_address);
    }
  };

  const handleLocationSave = () => {
    // All location types now save to parkingLocations
    const currentLocations = Array.isArray(data.parkingLocations) ? [...data.parkingLocations] : [];
    const locationType = locationDialogType === 'parking' ? 'crew' : locationDialogType; // 'parking' maps to 'crew' type
    const existingIndex = currentLocations.findIndex(loc => loc.type === locationType);
    
    const newLocation = {
      type: locationType,
      address: tempLocationValue,
      phone: '',
      notes: ''
    };
    
    if (existingIndex >= 0) {
      // Update existing
      currentLocations[existingIndex] = { ...currentLocations[existingIndex], address: tempLocationValue };
    } else {
      // Add new
      currentLocations.push(newLocation);
    }
    
    onUpdate({ parkingLocations: currentLocations });
    setLocationDialogOpen(false);
  };

  // Add producer handler
  const handleAddProducer = () => {
    if (!newProducerName.trim()) return;
    const current = Array.isArray(data.producers) ? data.producers : [];
    const next = [...current, { type: newProducerType, name: newProducerName.trim() }];
    onUpdate({ producers: next });
    setNewProducerName('');
    setNewProducerType('Executive Producer');
    setAddProducerOpen(false);
  };

  // Crew call handlers
  const handleUpdateCrewCall = (index, field, value) => {
    const current = Array.isArray(data.crewCalls) ? [...data.crewCalls] : [];
    if (index >= 0 && index < current.length) {
      current[index] = { ...current[index], [field]: value };
      onUpdate({ crewCalls: current });
    }
  };

  const handleDeleteCrewCall = (index) => {
    const current = Array.isArray(data.crewCalls) ? [...data.crewCalls] : [];
    if (index >= 0 && index < current.length) {
      current.splice(index, 1);
      onUpdate({ crewCalls: current });
    }
  };

  const handleAddCrewCall = (department) => {
    const current = Array.isArray(data.crewCalls) ? [...data.crewCalls] : [];
    current.push({
      department: department || 'Other',
      role: '',
      name: '',
      callTime: data.generalCallTime || '07:00'
    });
    onUpdate({ crewCalls: current });
  };

  // Add crew member from project crew
  const getCrewDisplayName = (crewMember) => {
    return crewMember?.name 
      || crewMember?.user?.name 
      || crewMember?.userName 
      || crewMember?.email 
      || crewMember?.phone 
      || 'Crew Member';
  };

  const handleAddFromProjectCrew = (crewMember) => {
    const current = Array.isArray(data.crewCalls) ? [...data.crewCalls] : [];
    // Check if already added
    const alreadyAdded = current.some(c => 
      (c.name || '') === getCrewDisplayName(crewMember) && c.department === (crewMember.department || 'Other')
    );
    if (alreadyAdded) return;
    
    current.push({
      department: crewMember.department || 'Other',
      role: crewMember.role || crewMember.position || crewMember.title || '',
      name: getCrewDisplayName(crewMember),
      phone: crewMember.phone || '',
      email: crewMember.email || '',
      callTime: data.generalCallTime || '07:00'
    });
    onUpdate({ crewCalls: current });
  };

  const handleAdvanceDay = (delta) => {
    const currentDay = data?.shootingDay || 1;
    const totalDays = data?.totalShootingDays || currentDay;
    let nextDay = currentDay + delta;
    if (nextDay < 1) nextDay = 1;
    if (nextDay > totalDays) nextDay = totalDays;
    if (nextDay === currentDay) return;
    onUpdate({ shootingDay: nextDay }, undefined, { skipUnsavedFlag: true, setUnsaved: false });
  };

  // Handle weather select from WeatherSearch dialog
  const handleWeatherSelect = (weatherNotes) => {
    // Parse sunrise/sunset from weather notes
    const parseSunTimesFromText = (text) => {
      const sunriseMatch = text.match(/Sunrise[:\s]*(\d{1,2}:\d{2}\s*(?:AM|PM)?)/i);
      const sunsetMatch = text.match(/Sunset[:\s]*(\d{1,2}:\d{2}\s*(?:AM|PM)?)/i);
      return {
        sunrise: sunriseMatch ? sunriseMatch[1].trim() : null,
        sunset: sunsetMatch ? sunsetMatch[1].trim() : null
      };
    };
    
    const timeToDate = (timeStr, baseDate) => {
      if (!timeStr || !baseDate) return undefined;
      try {
        const base = new Date(baseDate);
        const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
        if (!match) return undefined;
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3]?.toUpperCase();
        if (period === 'PM' && hours < 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        base.setHours(hours, minutes, 0, 0);
        return base.toISOString();
      } catch {
        return undefined;
      }
    };
    
    const { sunrise, sunset } = parseSunTimesFromText(weatherNotes);
    const sunriseISO = sunrise ? timeToDate(sunrise, data?.date) : undefined;
    const sunsetISO = sunset ? timeToDate(sunset, data?.date) : undefined;
    
    onUpdate({ 
      weatherNotes,
      generalInfo: {
        ...(data?.generalInfo || {}),
        ...(sunriseISO ? { sunriseTime: sunriseISO } : {}),
        ...(sunsetISO ? { sunsetTime: sunsetISO } : {})
      }
    });
    setWeatherSearchOpen(false);
  };

  // Handle schedule location change
  const handleScheduleLocationChange = (index, locationName) => {
    const selectedLocation = locations.find(loc => loc.name === locationName);
    const updatedSchedule = [...(data.schedule || [])];
    updatedSchedule[index] = {
      ...updatedSchedule[index],
      location: locationName,
      locationAddress: selectedLocation?.address || ''
    };
    console.log('Compose View: Updating schedule location', { index, locationName, updatedSchedule });
    onUpdate({ schedule: updatedSchedule });
  };

  // Handle updating a schedule field
  const handleUpdateSchedule = (index, field, value) => {
    const updatedSchedule = [...(data.schedule || [])];
    updatedSchedule[index] = { ...updatedSchedule[index], [field]: value };
    onUpdate({ schedule: updatedSchedule });
  };

  // Handle updating an advance schedule field
  const handleUpdateAdvanceSchedule = (index, field, value) => {
    const updatedSchedule = [...(data.nextDaySchedule || [])];
    updatedSchedule[index] = { ...updatedSchedule[index], [field]: value };
    onUpdate({ nextDaySchedule: updatedSchedule });
  };

  // Handle advance schedule location change
  const handleAdvanceScheduleLocationChange = (index, locationName) => {
    const selectedLocation = locations.find(loc => loc.name === locationName);
    const updatedSchedule = [...(data.nextDaySchedule || [])];
    updatedSchedule[index] = {
      ...updatedSchedule[index],
      location: locationName,
      locationAddress: selectedLocation?.address || ''
    };
    console.log('Compose View: Updating nextDaySchedule location', { index, locationName, updatedSchedule });
    onUpdate({ nextDaySchedule: updatedSchedule });
  };

  // Handle adding talent from project cast
  const handleAddTalentFromCast = (castMember) => {
    const newTalentCall = {
      name: castMember.name,
      character: castMember.characterName,
      castNumber: castMember.hierarchy || castMember.castNumber || '',
      callTime: '',
      setCallTime: '',
      pickupTime: '',
      status: 'SW',
      notes: ''
    };
    const existingCalls = Array.isArray(data.talentCalls) ? data.talentCalls : [];
    onUpdate({ talentCalls: [...existingCalls, newTalentCall] });
    setAddTalentDialogOpen(false);
  };

  // Handle adding a blank talent call
  const handleAddBlankTalent = () => {
    const newTalentCall = {
      name: '',
      character: '',
      castNumber: '',
      callTime: '',
      setCallTime: '',
      pickupTime: '',
      status: 'SW',
      notes: ''
    };
    const existingCalls = Array.isArray(data.talentCalls) ? data.talentCalls : [];
    onUpdate({ talentCalls: [...existingCalls, newTalentCall] });
  };

  // Handle deleting a talent call
  const handleDeleteTalent = (index) => {
    const existingCalls = Array.isArray(data.talentCalls) ? data.talentCalls : [];
    const updatedCalls = [...existingCalls];
    updatedCalls.splice(index, 1);
    onUpdate({ talentCalls: updatedCalls });
  };

  // Handle updating a talent call field
  const handleUpdateTalent = (index, field, value) => {
    const existingCalls = Array.isArray(data.talentCalls) ? data.talentCalls : [];
    const updatedCalls = [...existingCalls];
    updatedCalls[index] = { ...updatedCalls[index], [field]: value };
    onUpdate({ talentCalls: updatedCalls });
  };

  // Get hierarchy color for cast badge
  const getHierarchyColor = (hierarchy) => {
    switch(Number(hierarchy)) {
      case 1: return '#FFD700'; // Lead - Gold
      case 2: return '#C0C0C0'; // Supporting - Silver
      case 3: return '#CD7F32'; // Featured - Bronze
      case 4: return '#4682B4'; // Minor Supporting - Steel Blue
      case 5: return '#708090'; // Minor - Slate Gray
      case 6: return '#A9A9A9'; // Extra - Dark Gray
      case 7: return '#D3D3D3'; // Background - Light Gray
      default: return '#708090';
    }
  };

  // Helper to get parking location by type
  const getParkingLocationByType = (type) => {
    if (Array.isArray(data.parkingLocations)) {
      return data.parkingLocations.find(loc => loc.type === type);
    }
    return null;
  };

  // Get crew parking display - from parkingLocations where type is 'crew'
  const getCrewParkingDisplay = () => {
    const crewParking = getParkingLocationByType('crew');
    if (crewParking) {
      const parts = [];
      if (crewParking.address) parts.push(crewParking.address);
      if (crewParking.phone) parts.push(crewParking.phone);
      return parts.join(' • ') || crewParking.notes || '';
    }
    // Fallback to parkingNotes
    return data.parkingNotes || '';
  };

  // Get basecamp display - from parkingLocations where type is 'basecamp'
  const getBasecampDisplay = () => {
    const basecamp = getParkingLocationByType('basecamp');
    if (basecamp) {
      const parts = [];
      if (basecamp.address) parts.push(basecamp.address);
      if (basecamp.phone) parts.push(basecamp.phone);
      return parts.join(' • ') || basecamp.notes || '';
    }
    // Fallback to mainLocation
    return data.mainLocation || '';
  };

  // Get production office display - from parkingLocations where type is 'productionOffice'
  const getProductionOfficeDisplay = () => {
    const prodOffice = getParkingLocationByType('productionOffice');
    if (prodOffice) {
      const parts = [];
      if (prodOffice.address) parts.push(prodOffice.address);
      if (prodOffice.phone) parts.push(prodOffice.phone);
      return parts.join(' • ') || prodOffice.notes || '';
    }
    // Fallback to emergencyContacts.productionOffice
    return data.emergencyContacts?.productionOffice || '';
  };

  // Section styles
  const sectionStyle = {
    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
    borderRadius: 1,
    p: 2,
    mb: 2,
    backgroundColor: theme.palette.background.paper
  };

  const headerCellStyle = {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    py: 1
  };

  return (
    <Box sx={{
      maxWidth: 1100,
      mx: 'auto',
      p: 3,
      backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
      minHeight: '100vh',
      overflow: 'auto'
    }}>
      {/* Print Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Tooltip title="Print call sheet" arrow>
          <span>
            <IconButton
              onClick={onPrint}
              aria-label="Print call sheet"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                border: `1px solid ${alpha('#fff', 0.2)}`,
                color: alpha('#fff', 0.9),
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: alpha('#fff', 0.12)
                }
              }}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Main Callsheet Paper */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1
        }}
      >
        {/* Page Title */}
        <Typography 
          variant="h4" 
          align="center" 
          sx={{ 
            fontWeight: 800, 
            textTransform: 'uppercase',
            mb: 3,
            letterSpacing: 1
          }}
        >
          <EditableField
            value={data.projectTitle}
            onChange={(val) => handleFieldUpdate('projectTitle', val)}
            label="Project Title"
            sx={{ fontWeight: 800, fontSize: '1.75rem' }}
          />
        </Typography>

        {/* Main Header - 3 Column Layout */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {/* Left Column - Production Info */}
          <Box sx={{ flex: 1, ...sectionStyle }}>
            {/* Logo or Company Name */}
            {data.productionLogo ? (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <img 
                  src={data.productionLogo} 
                  alt="Production Logo" 
                  style={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain' }}
                />
              </Box>
            ) : (
              <Typography variant="subtitle2" align="center" sx={{ fontWeight: 700, mb: 2 }}>
                <EditableField
                  value={data.company}
                  onChange={(val) => handleFieldUpdate('company', val)}
                  label="Production Company"
                />
              </Typography>
            )}
            
            <Box sx={{ fontSize: '0.85rem' }}>
              <Box sx={{ mb: 0.5 }}>
                <strong>Director:</strong>{' '}
                <EditableField
                  value={data.director}
                  onChange={(val) => handleFieldUpdate('director', val)}
                  label="Director"
                />
              </Box>
              <Box sx={{ mb: 0.5 }}>
                <strong>Producer:</strong>{' '}
                <EditableField
                  value={data.producer}
                  onChange={(val) => handleFieldUpdate('producer', val)}
                  label="Producer"
                />
              </Box>
              {/* Additional Producers */}
              {Array.isArray(data.producers) && data.producers.length > 0 && (
                data.producers.map((p, idx) => (
                  <Box key={idx} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <strong>{p.type || 'Producer'}:</strong>{' '}
                    <Typography component="span" sx={{ fontSize: '0.85rem' }}>
                      {p.name}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        const next = data.producers.filter((_, i) => i !== idx);
                        onUpdate({ producers: next });
                      }}
                      sx={{ 
                        p: 0.25, 
                        ml: 0.5,
                        opacity: 0.5,
                        '&:hover': { opacity: 1, color: 'error.main' }
                      }}
                    >
                      <Typography sx={{ fontSize: '0.7rem' }}>✕</Typography>
                    </IconButton>
                  </Box>
                ))
              )}
              {/* Add Producer Button */}
              <Box 
                sx={{ 
                  mb: 0.5, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  cursor: 'pointer',
                  color: 'primary.main',
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
                onClick={() => setAddProducerOpen(true)}
              >
                <Typography sx={{ fontSize: '0.75rem' }}>+ Add Producer</Typography>
              </Box>
              <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <strong>Production Office:</strong>{' '}
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontSize: '0.85rem',
                      color: getProductionOfficeDisplay() ? 'text.primary' : 'text.disabled',
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' }
                    }}
                    onClick={() => openLocationDialog('productionOffice', getProductionOfficeDisplay())}
                  >
                    {getProductionOfficeDisplay() || 'Click to set...'}
                  </Typography>
                </Box>
                <Tooltip title="Search location">
                  <IconButton 
                    size="small" 
                    onClick={() => openLocationDialog('productionOffice', getProductionOfficeDisplay())}
                    sx={{ 
                      p: 0.5,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      '&:hover': { backgroundColor: alpha(theme.palette.secondary.main, 0.2) }
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Center Column - General Crew Call */}
          <Box sx={{ 
            flex: 1.2, 
            ...sectionStyle,
            border: `2px solid ${theme.palette.text.primary}`,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 2 }}>
              GENERAL CREW CALL
            </Typography>
            <Box sx={{ my: 1 }}>
              <EditableTimeField
                value={data.generalCallTime}
                onChange={(val) => handleFieldUpdate('generalCallTime', val)}
                label="General Call Time"
              />
            </Box>
            {data.safetyNotes && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block',
                  borderTop: `1px dashed ${theme.palette.divider}`,
                  pt: 1,
                  mt: 1,
                  color: 'warning.main'
                }}
              >
                <strong>SAFETY:</strong> {data.safetyNotes.substring(0, 100)}
                {data.safetyNotes.length > 100 ? '...' : ''}
              </Typography>
            )}
          </Box>

          {/* Right Column - Date & Weather */}
          <Box sx={{ flex: 1, ...sectionStyle }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {formattedDate}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <IconButton 
                size="small" 
                onClick={() => handleAdvanceDay(-1)} 
                disabled={(data?.shootingDay || 1) <= 1}
                sx={{ 
                  border: (theme) => `1px solid ${theme.palette.divider}`, 
                  bgcolor: 'background.paper',
                  '&:disabled': { opacity: 0.4 }
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                DAY {data.shootingDay || 1} of {data.totalShootingDays || 1}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => handleAdvanceDay(1)} 
                disabled={(data?.shootingDay || 1) >= (data?.totalShootingDays || data?.shootingDay || 1)}
                sx={{ 
                  border: (theme) => `1px solid ${theme.palette.divider}`, 
                  bgcolor: 'background.paper',
                  '&:disabled': { opacity: 0.4 }
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Weather Card */}
            <Box sx={{ 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1.5,
              backgroundColor: alpha(theme.palette.info.main, 0.05),
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.1) }
            }}
            onClick={() => setWeatherSearchOpen(true)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '1.5rem' }}>{condIcon}</Typography>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      {wx.tempVal ? `${wx.tempVal}°${wx.tempUnit || 'F'}` : '--°F'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {wx.condition || 'Click to get weather'}
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title="Get Weather">
                  <IconButton 
                    size="small" 
                    onClick={(e) => { e.stopPropagation(); setWeatherSearchOpen(true); }}
                    sx={{ 
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                      '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.2) }
                    }}
                  >
                    <WbSunnyIcon sx={{ fontSize: 16, color: 'info.main' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, fontSize: '0.75rem' }}>
                {wx.windVal && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AirIcon sx={{ fontSize: 14 }} />
                    {wx.windVal} {wx.windUnit || 'mph'}
                  </Box>
                )}
                {wx.humidity && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WaterDropIcon sx={{ fontSize: 14 }} />
                    {wx.humidity}%
                  </Box>
                )}
              </Box>
            </Box>

            {/* Sunrise/Sunset */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, fontSize: '0.8rem' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WbSunnyIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                <span>{data.generalInfo?.sunriseTime ? formatTimeDisplay(data.generalInfo.sunriseTime) : '--:--'}</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <NightsStayIcon sx={{ fontSize: 16, color: 'info.main' }} />
                <span>{data.generalInfo?.sunsetTime ? formatTimeDisplay(data.generalInfo.sunsetTime) : '--:--'}</span>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Info Strip - Hospital, Parking, Basecamp */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {/* Hospital - with Google Maps search */}
          <Box sx={{ flex: 1, ...sectionStyle, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalHospitalIcon sx={{ fontSize: 18, color: 'error.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>NEAREST HOSPITAL</Typography>
              </Box>
              <Tooltip title="Search for nearby hospitals">
                <IconButton 
                  size="small" 
                  onClick={() => setHospitalSearchOpen(true)}
                  sx={{ 
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.2) }
                  }}
                >
                  <SearchIcon sx={{ fontSize: 16, color: 'error.main' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.85rem',
                color: data.emergencyContacts?.hospital ? 'text.primary' : 'text.disabled',
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' }
              }}
              onClick={() => setHospitalSearchOpen(true)}
            >
              {data.emergencyContacts?.hospital || 'Click to search for hospitals...'}
            </Typography>
          </Box>

          {/* Parking - with location search */}
          <Box sx={{ flex: 1, ...sectionStyle, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalParkingIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>CREW PARKING</Typography>
              </Box>
              <Tooltip title="Search location">
                <IconButton 
                  size="small" 
                  onClick={() => openLocationDialog('parking', getCrewParkingDisplay())}
                  sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) }
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.85rem',
                color: getCrewParkingDisplay() ? 'text.primary' : 'text.disabled',
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' }
              }}
              onClick={() => openLocationDialog('parking', getCrewParkingDisplay())}
            >
              {getCrewParkingDisplay() || 'Click to add parking location...'}
            </Typography>
          </Box>

          {/* Basecamp - with location search */}
          <Box sx={{ flex: 1, ...sectionStyle, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeWorkIcon sx={{ fontSize: 18, color: 'success.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>BASECAMP</Typography>
              </Box>
              <Tooltip title="Search location">
                <IconButton 
                  size="small" 
                  onClick={() => openLocationDialog('basecamp', getBasecampDisplay())}
                  sx={{ 
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.2) }
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: 16, color: 'success.main' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.85rem',
                color: getBasecampDisplay() ? 'text.primary' : 'text.disabled',
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' }
              }}
              onClick={() => openLocationDialog('basecamp', getBasecampDisplay())}
            >
              {getBasecampDisplay() || 'Click to add basecamp location...'}
            </Typography>
          </Box>
        </Box>

        {/* Schedule Table */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, borderBottom: `2px solid ${theme.palette.text.primary}`, pb: 0.5 }}>
            SHOOTING SCHEDULE
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { border: `1px solid ${theme.palette.divider}` } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: 60 }}>SCENE</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 50 }}>I/E</TableCell>
                  <TableCell sx={headerCellStyle}>DESCRIPTION</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 50 }}>D/N</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 80 }}>CAST</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 60 }}>PAGES</TableCell>
                  <TableCell sx={headerCellStyle}>LOCATION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(data.schedule) && data.schedule.length > 0 ? (
                  data.schedule.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          value={item.sceneNumber || ''}
                          onChange={(e) => handleUpdateSchedule(index, 'sceneNumber', e.target.value)}
                          variant="standard"
                          inputProps={{ style: { textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 } }}
                          sx={{ width: 50 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Select
                          size="small"
                          value={item.location_type || ''}
                          onChange={(e) => handleUpdateSchedule(index, 'location_type', e.target.value)}
                          variant="standard"
                          sx={{ fontSize: '0.8rem', minWidth: 45 }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="INT">INT</MenuItem>
                          <MenuItem value="EXT">EXT</MenuItem>
                          <MenuItem value="I/E">I/E</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.description || ''}
                          onChange={(e) => handleUpdateSchedule(index, 'description', e.target.value)}
                          variant="standard"
                          fullWidth
                          multiline
                          maxRows={3}
                          inputProps={{ style: { fontSize: '0.8rem' } }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Select
                          size="small"
                          value={item.dayNight || ''}
                          onChange={(e) => handleUpdateSchedule(index, 'dayNight', e.target.value)}
                          variant="standard"
                          sx={{ fontSize: '0.7rem', minWidth: 60 }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="DAY">DAY</MenuItem>
                          <MenuItem value="NIGHT">NIGHT</MenuItem>
                          <MenuItem value="MORNING">MORNING</MenuItem>
                          <MenuItem value="EVENING">EVENING</MenuItem>
                          <MenuItem value="DAWN">DAWN</MenuItem>
                          <MenuItem value="DUSK">DUSK</MenuItem>
                          <MenuItem value="SUNSET">SUNSET</MenuItem>
                          <MenuItem value="SUNRISE">SUNRISE</MenuItem>
                          <MenuItem value="CONTINUOUS">CONT.</MenuItem>
                          <MenuItem value="LATER">LATER</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {getCastNumbers(item.cast) || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          value={item.pages || ''}
                          onChange={(e) => handleUpdateSchedule(index, 'pages', e.target.value)}
                          variant="standard"
                          inputProps={{ style: { textAlign: 'center', fontSize: '0.8rem' } }}
                          sx={{ width: 50 }}
                          placeholder="0/8"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={item.location || ''}
                            onChange={(e) => handleScheduleLocationChange(index, e.target.value)}
                            displayEmpty
                            variant="standard"
                            sx={{ fontSize: '0.8rem' }}
                          >
                            <MenuItem value="">
                              <Typography variant="body2" color="text.secondary">Select location</Typography>
                            </MenuItem>
                            {(item.location && !locations.some(loc => loc.name === item.location)) && (
                              <MenuItem value={item.location}>
                                <Typography variant="body2">{item.location}</Typography>
                              </MenuItem>
                            )}
                            {locations.map((loc, i) => (
                              <MenuItem key={i} value={loc.name}>
                                <Typography variant="body2">{loc.name}</Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {item.locationAddress && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                            {item.locationAddress}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No scenes scheduled. Go to the Schedule tab to add scenes.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Talent Calls Table */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.4)}`, pb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              TALENT CALLS
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {projectCast.length > 0 && (
                <Tooltip title="Add from cast" arrow>
                  <span>
                    <IconButton
                      onClick={() => setAddTalentDialogOpen(true)}
                      aria-label="Add from cast"
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1,
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                        color: theme.palette.text.primary,
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.text.primary, 0.1)
                        }
                      }}
                    >
                      <PersonAddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Add talent row" arrow>
                <span>
                  <IconButton
                    onClick={handleAddBlankTalent}
                    aria-label="Add talent row"
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                      color: theme.palette.text.primary,
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.text.primary, 0.1)
                      }
                    }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { border: `1px solid ${theme.palette.divider}` } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: 40 }}>#</TableCell>
                  <TableCell sx={headerCellStyle}>CHARACTER</TableCell>
                  <TableCell sx={headerCellStyle}>CAST</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 60 }}>STATUS</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 80 }}>PICKUP</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 80 }}>CALL</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 80 }}>SET</TableCell>
                  <TableCell sx={headerCellStyle}>NOTES</TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(data.talentCalls) && data.talentCalls.length > 0 ? (
                  data.talentCalls.map((talent, index) => (
                    <TableRow key={index} hover>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          value={talent.castNumber || ''}
                          onChange={(e) => handleUpdateTalent(index, 'castNumber', e.target.value)}
                          variant="standard"
                          inputProps={{ style: { textAlign: 'center', fontSize: '0.8rem' } }}
                          sx={{ width: 40 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={talent.character || ''}
                          onChange={(e) => handleUpdateTalent(index, 'character', e.target.value)}
                          variant="standard"
                          fullWidth
                          inputProps={{ style: { fontSize: '0.8rem' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={talent.name || ''}
                          onChange={(e) => handleUpdateTalent(index, 'name', e.target.value)}
                          variant="standard"
                          fullWidth
                          inputProps={{ style: { fontSize: '0.8rem' } }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Select
                          size="small"
                          value={talent.status || 'SW'}
                          onChange={(e) => handleUpdateTalent(index, 'status', e.target.value)}
                          variant="standard"
                          sx={{ fontSize: '0.8rem', minWidth: 50 }}
                        >
                          <MenuItem value="SW">SW</MenuItem>
                          <MenuItem value="W">W</MenuItem>
                          <MenuItem value="H">H</MenuItem>
                          <MenuItem value="R">R</MenuItem>
                          <MenuItem value="F">F</MenuItem>
                          <MenuItem value="ND">ND</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="time"
                          value={talent.pickupTime || ''}
                          onChange={(e) => handleUpdateTalent(index, 'pickupTime', e.target.value)}
                          variant="standard"
                          inputProps={{ style: { textAlign: 'center', fontSize: '0.75rem', colorScheme: theme.palette.mode } }}
                          sx={{ width: 80, '& input': { color: 'text.primary' } }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="time"
                          value={talent.callTime || ''}
                          onChange={(e) => handleUpdateTalent(index, 'callTime', e.target.value)}
                          variant="standard"
                          inputProps={{ style: { textAlign: 'center', fontSize: '0.75rem', colorScheme: theme.palette.mode } }}
                          sx={{ width: 80, '& input': { color: 'text.primary' } }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="time"
                          value={talent.setCallTime || ''}
                          onChange={(e) => handleUpdateTalent(index, 'setCallTime', e.target.value)}
                          variant="standard"
                          inputProps={{ style: { textAlign: 'center', fontSize: '0.75rem', colorScheme: theme.palette.mode } }}
                          sx={{ width: 80, '& input': { color: 'text.primary' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={talent.notes || ''}
                          onChange={(e) => handleUpdateTalent(index, 'notes', e.target.value)}
                          variant="standard"
                          fullWidth
                          inputProps={{ style: { fontSize: '0.8rem' } }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteTalent(index)}
                          sx={{ color: 'error.main', opacity: 0.7, '&:hover': { opacity: 1 } }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No talent scheduled. Click "Add Talent" or "Add from Cast" to add cast members.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Background / Stand-ins */}
        {Array.isArray(data.background) && data.background.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, borderBottom: `2px solid ${theme.palette.text.primary}`, pb: 0.5 }}>
              BACKGROUND / STAND-INS
            </Typography>
            <TableContainer>
              <Table size="small" sx={{ '& td, & th': { border: `1px solid ${theme.palette.divider}` } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellStyle, width: 80 }}>QTY</TableCell>
                    <TableCell sx={headerCellStyle}>DESCRIPTION</TableCell>
                    <TableCell sx={{ ...headerCellStyle, width: 100 }}>CALL TIME</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.background.map((bg, index) => (
                    <TableRow key={index} hover>
                      <TableCell align="center">{bg.qty || ''}</TableCell>
                      <TableCell>{bg.description || bg.notes || ''}</TableCell>
                      <TableCell align="center">{bg.call || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Special Instructions / Department Notes */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, borderBottom: `2px solid ${theme.palette.text.primary}`, pb: 0.5 }}>
            SPECIAL INSTRUCTIONS
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { border: `1px solid ${theme.palette.divider}` } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: '20%' }}>DEPARTMENT</TableCell>
                  <TableCell sx={headerCellStyle}>INSTRUCTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { dept: 'Camera', key: 'cameraNotes' },
                  { dept: 'Lighting', key: 'lightingNotes' },
                  { dept: 'Props', key: 'propsNotes' },
                  { dept: 'Sound', key: 'soundNotes' },
                  { dept: 'VFX', key: 'vfxNotes' },
                  { dept: 'Special FX', key: 'specialFxNotes' },
                  { dept: 'Vehicles', key: 'vehiclesNotes' },
                  { dept: 'Stunts', key: 'stuntsNotes' },
                  { dept: 'Animals', key: 'animalsNotes' },
                  { dept: 'Hair/Makeup', key: 'hairMakeupNotes' },
                  { dept: 'Misc', key: 'miscNotes' }
                ].map(({ dept, key }) => (
                  <TableRow key={key} hover>
                    <TableCell sx={{ fontWeight: 600, verticalAlign: 'top', pt: 1.5 }}>{dept}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={data[key] || ''}
                        onChange={(e) => onUpdate({ [key]: e.target.value })}
                        variant="standard"
                        fullWidth
                        multiline
                        placeholder={`Add ${dept.toLowerCase()} instructions...`}
                        inputProps={{ style: { fontSize: '0.85rem' } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Advance Schedule */}
        {Array.isArray(data.nextDaySchedule) && data.nextDaySchedule.length > 0 && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.info.main, 0.03)
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: 'info.main' }}>
              ADVANCE SCHEDULE - DAY {(data.shootingDay || 0) + 1}
            </Typography>
            <TableContainer>
              <Table size="small" sx={{ '& td, & th': { border: `1px solid ${theme.palette.divider}` } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellStyle, width: 60 }}>SCENE</TableCell>
                    <TableCell sx={{ ...headerCellStyle, width: 50 }}>I/E</TableCell>
                    <TableCell sx={headerCellStyle}>DESCRIPTION</TableCell>
                    <TableCell sx={{ ...headerCellStyle, width: 50 }}>D/N</TableCell>
                    <TableCell sx={{ ...headerCellStyle, width: 80 }}>CAST</TableCell>
                    <TableCell sx={{ ...headerCellStyle, width: 60 }}>PAGES</TableCell>
                    <TableCell sx={headerCellStyle}>LOCATION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.nextDaySchedule.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          value={item.sceneNumber || ''}
                          onChange={(e) => handleUpdateAdvanceSchedule(index, 'sceneNumber', e.target.value)}
                          variant="standard"
                          inputProps={{ style: { textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 } }}
                          sx={{ width: 50 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Select
                          size="small"
                          value={item.location_type || ''}
                          onChange={(e) => handleUpdateAdvanceSchedule(index, 'location_type', e.target.value)}
                          variant="standard"
                          sx={{ fontSize: '0.8rem', minWidth: 45 }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="INT">INT</MenuItem>
                          <MenuItem value="EXT">EXT</MenuItem>
                          <MenuItem value="I/E">I/E</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.description || ''}
                          onChange={(e) => handleUpdateAdvanceSchedule(index, 'description', e.target.value)}
                          variant="standard"
                          fullWidth
                          multiline
                          maxRows={3}
                          inputProps={{ style: { fontSize: '0.8rem' } }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Select
                          size="small"
                          value={item.dayNight || ''}
                          onChange={(e) => handleUpdateAdvanceSchedule(index, 'dayNight', e.target.value)}
                          variant="standard"
                          sx={{ fontSize: '0.7rem', minWidth: 60 }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="DAY">DAY</MenuItem>
                          <MenuItem value="NIGHT">NIGHT</MenuItem>
                          <MenuItem value="MORNING">MORNING</MenuItem>
                          <MenuItem value="EVENING">EVENING</MenuItem>
                          <MenuItem value="DAWN">DAWN</MenuItem>
                          <MenuItem value="DUSK">DUSK</MenuItem>
                          <MenuItem value="SUNSET">SUNSET</MenuItem>
                          <MenuItem value="SUNRISE">SUNRISE</MenuItem>
                          <MenuItem value="CONTINUOUS">CONT.</MenuItem>
                          <MenuItem value="LATER">LATER</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {getCastNumbers(item.cast) || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          value={item.pages || ''}
                          onChange={(e) => handleUpdateAdvanceSchedule(index, 'pages', e.target.value)}
                          variant="standard"
                          inputProps={{ style: { textAlign: 'center', fontSize: '0.8rem' } }}
                          sx={{ width: 50 }}
                          placeholder="0/8"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={item.location || ''}
                            onChange={(e) => handleAdvanceScheduleLocationChange(index, e.target.value)}
                            displayEmpty
                            variant="standard"
                            sx={{ fontSize: '0.8rem' }}
                          >
                            <MenuItem value="">
                              <Typography variant="body2" color="text.secondary">Select location</Typography>
                            </MenuItem>
                            {(item.location && !locations.some(loc => loc.name === item.location)) && (
                              <MenuItem value={item.location}>
                                <Typography variant="body2">{item.location}</Typography>
                              </MenuItem>
                            )}
                            {locations.map((loc, i) => (
                              <MenuItem key={i} value={loc.name}>
                                <Typography variant="body2">{loc.name}</Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {item.locationAddress && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                            {item.locationAddress}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`, 
          pt: 2, 
          mt: 3,
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: '0.75rem'
        }}>
          {data.projectTitle || 'Untitled Project'} - Call Sheet #{data.shootingDay || 1} - {formattedDate}
        </Box>
      </Paper>

      {/* Page 2 - Crew Calls Preview */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          mt: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1
        }}
      >
        <Typography variant="h5" align="center" sx={{ fontWeight: 700, mb: 3, borderBottom: `2px solid ${theme.palette.text.primary}`, pb: 1 }}>
          CREW CALLS
        </Typography>

        {/* Crew by Department Grid */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: 2
        }}>
          {Object.entries(crewByDepartment).map(([dept, members]) => {
            // Get the actual indices of these crew members in the full crewCalls array
            const getCrewIndex = (crew) => {
              return data.crewCalls?.findIndex(c => c === crew) ?? -1;
            };
            
            return (
              <Box key={dept} sx={sectionStyle}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 700, 
                      textTransform: 'uppercase',
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      p: 1,
                      borderRadius: 1,
                      flex: 1
                    }}
                  >
                    {dept}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => handleAddCrewCall(dept)}
                    sx={{ ml: 1 }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5 }}>Call</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5, width: 40 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((crew, idx) => {
                      const crewIndex = getCrewIndex(crew);
                      return (
                        <TableRow key={idx}>
                          <TableCell sx={{ py: 0.5 }}>
                            <TextField
                              size="small"
                              value={crew.role || ''}
                              onChange={(e) => handleUpdateCrewCall(crewIndex, 'role', e.target.value)}
                              variant="standard"
                              inputProps={{ style: { fontSize: '0.8rem' } }}
                              fullWidth
                              placeholder="Title"
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <TextField
                              size="small"
                              value={crew.name || ''}
                              onChange={(e) => handleUpdateCrewCall(crewIndex, 'name', e.target.value)}
                              variant="standard"
                              inputProps={{ style: { fontSize: '0.8rem' } }}
                              fullWidth
                              placeholder="Name"
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <TextField
                              size="small"
                              type="time"
                              value={crew.callTime || '07:00'}
                              onChange={(e) => handleUpdateCrewCall(crewIndex, 'callTime', e.target.value)}
                              variant="standard"
                              inputProps={{ style: { fontSize: '0.8rem' } }}
                              sx={{ width: 90 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteCrewCall(crewIndex)}
                              sx={{ color: 'error.main', p: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            );
          })}
        </Box>

        {/* Add Crew Buttons */}
        <Box sx={{ mt: 2, display: 'flex', gap: 0.75, justifyContent: 'center' }}>
          <Tooltip title="Add crew row" arrow>
            <span>
              <IconButton
                onClick={() => handleAddCrewCall('Other')}
                aria-label="Add crew row"
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                  color: theme.palette.text.primary,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.1)
                  }
                }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          {projectCrew.length > 0 && (
            <Tooltip title="Add from project crew" arrow>
              <span>
                <IconButton
                  onClick={() => setAddCrewDialogOpen(true)}
                  aria-label="Add crew from project"
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.text.primary, 0.1)
                    }
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        {Object.keys(crewByDepartment).length === 0 && (
          <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', fontSize: '0.85rem' }}>
            No crew calls added yet.
          </Box>
        )}

        {/* Emergency Contacts */}
        <Box sx={{ mt: 3, ...sectionStyle }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            EMERGENCY CONTACTS
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, fontSize: '0.85rem' }}>
            <Box><strong>Emergency:</strong> {data.emergencyContacts?.police || data.emergencyContacts?.fire || '911'}</Box>
            <Box><strong>Set Medic:</strong> {data.emergencyContacts?.setMedic || 'Not specified'}</Box>
            <Box><strong>Location Manager:</strong> {data.emergencyContacts?.locationManager || 'Not specified'}</Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`, 
          pt: 2, 
          mt: 3,
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: '0.75rem'
        }}>
          {data.projectTitle || 'Untitled Project'} - Crew Calls - {formattedDate}
        </Box>
      </Paper>

      {/* Weather Search Dialog */}
      <WeatherSearch
        open={weatherSearchOpen}
        onClose={() => setWeatherSearchOpen(false)}
        onSelectWeather={handleWeatherSelect}
        initialLocation={data.mainLocation || ''}
      />

      {/* Hospital Search Dialog */}
      <HospitalSearch
        open={hospitalSearchOpen}
        onClose={() => setHospitalSearchOpen(false)}
        onSelect={handleSelectHospital}
        initialLocation={data.mainLocationAddress || data.mainLocation || ''}
      />

      {/* Location Search Dialog */}
      <Dialog 
        open={locationDialogOpen} 
        onClose={() => setLocationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {locationDialogType === 'basecamp' ? 'Set Basecamp Location' : 
           locationDialogType === 'productionOffice' ? 'Set Production Office Location' : 
           'Set Crew Parking Location'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search for a location using Google Maps or enter an address manually.
            </Typography>
            <GooglePlacesAutocomplete
              value={tempLocationValue}
              onChange={setTempLocationValue}
              onPlaceSelect={handleLocationSelect}
              label={locationDialogType === 'basecamp' ? 'Basecamp Address' : 
                     locationDialogType === 'productionOffice' ? 'Production Office Address' : 
                     'Parking Address'}
              placeholder="Search for an address..."
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              value={tempLocationValue}
              onChange={(e) => setTempLocationValue(e.target.value)}
              label="Location Details"
              placeholder="Add additional details or notes..."
              sx={{ mt: 2 }}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button 
            color="error" 
            onClick={() => setTempLocationValue('')}
            startIcon={<DeleteIcon />}
          >
            Clear
          </Button>
          <Box>
            <Button onClick={() => setLocationDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleLocationSave}>Save</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add Producer Dialog */}
      <Dialog 
        open={addProducerOpen} 
        onClose={() => setAddProducerOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Producer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Select
              fullWidth
              value={newProducerType}
              onChange={(e) => setNewProducerType(e.target.value)}
              size="small"
            >
              <MenuItem value="Executive Producer">Executive Producer</MenuItem>
              <MenuItem value="Co-Producer">Co-Producer</MenuItem>
              <MenuItem value="Associate Producer">Associate Producer</MenuItem>
              <MenuItem value="Line Producer">Line Producer</MenuItem>
              <MenuItem value="Supervising Producer">Supervising Producer</MenuItem>
              <MenuItem value="Producer">Producer</MenuItem>
            </Select>
            <TextField
              fullWidth
              size="small"
              value={newProducerName}
              onChange={(e) => setNewProducerName(e.target.value)}
              label="Name"
              placeholder="Enter producer name..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddProducer();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddProducerOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddProducer}
            disabled={!newProducerName.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Talent from Cast Dialog */}
      <Dialog 
        open={addTalentDialogOpen} 
        onClose={() => setAddTalentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Talent from Project Cast</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a cast member to add to the talent calls.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflow: 'auto' }}>
              {projectCast.map((castMember) => (
                <Paper
                  key={castMember._id}
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      borderColor: theme.palette.primary.main
                    }
                  }}
                  onClick={() => handleAddTalentFromCast(castMember)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: getHierarchyColor(castMember.hierarchy),
                        color: '#000',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        border: '1px solid rgba(0,0,0,0.2)',
                        flexShrink: 0
                      }}
                    >
                      {castMember.hierarchy || '?'}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {castMember.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        as <strong>{castMember.characterName}</strong>
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
              {projectCast.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No cast members found. Add cast in the Cast tab first.
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTalentDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Add Crew from Project Crew Dialog */}
      <Dialog 
        open={addCrewDialogOpen} 
        onClose={() => setAddCrewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add from Project Crew</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select crew members to add to the call sheet.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflow: 'auto' }}>
              {projectCrew.map((crewMember, idx) => {
                const displayName = getCrewDisplayName(crewMember);
                const alreadyAdded = data.crewCalls?.some(c => 
                  (c.name || '') === displayName && c.department === (crewMember.department || 'Other')
                );
                return (
                  <Paper
                    key={crewMember._id || idx}
                    elevation={0}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      cursor: alreadyAdded ? 'default' : 'pointer',
                      opacity: alreadyAdded ? 0.5 : 1,
                      '&:hover': alreadyAdded ? {} : {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.main
                      }
                    }}
                    onClick={() => !alreadyAdded && handleAddFromProjectCrew(crewMember)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {crewMember.role || crewMember.position || crewMember.title || 'Crew'} • {crewMember.department || 'Other'}
                        </Typography>
                      </Box>
                      {alreadyAdded ? (
                        <Chip label="Added" size="small" color="success" variant="outlined" />
                      ) : (
                        <IconButton size="small" color="primary">
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                );
              })}
              {projectCrew.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No crew members found. Add crew in the Crew tab first.
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCrewDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CallsheetComposeView;
